import { useCallback, useEffect, useRef, useState } from "react";
import { getMessages } from "../api";
import type { MailDirectionFilter, MailListView, MailSummary, MailTaskFilter } from "../types";

/**
 * Pages through a mail list, newest first. Changing the view, search, direction or task filter starts again from
 * the first page, and a response for an earlier request is discarded if a newer one has started.
 */
export const useMailList = (
  listPath: string,
  view: MailListView,
  search: string,
  direction: MailDirectionFilter,
  tasks: MailTaskFilter,
) => {
  const [items, setItems] = useState<MailSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const loadFirstPage = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setIsLoading(true);
    setIsLoadingMore(false);
    setError(null);

    try {
      const page = await getMessages(listPath, { view, direction, tasks, search });
      if (currentRequest !== requestId.current) return;

      setItems(page.items);
      setHasMore(page.hasMore);
      setNextCursor(page.nextCursor);
    } catch {
      if (currentRequest !== requestId.current) return;
      setItems([]);
      setHasMore(false);
      setNextCursor(null);
      setError("We couldn't load these emails.");
    } finally {
      if (currentRequest === requestId.current) setIsLoading(false);
    }
  }, [listPath, view, search, direction, tasks]);

  const loadMore = useCallback(async () => {
    if (!hasMore || !nextCursor || isLoading || isLoadingMore) return;

    const currentRequest = requestId.current;
    setIsLoadingMore(true);
    setError(null);

    try {
      const page = await getMessages(listPath, { view, direction, tasks, search, cursor: nextCursor });
      if (currentRequest !== requestId.current) return;

      setItems(previous => [...previous, ...page.items]);
      setHasMore(page.hasMore);
      setNextCursor(page.nextCursor);
    } catch {
      if (currentRequest !== requestId.current) return;
      setError("We couldn't load more emails.");
    } finally {
      if (currentRequest === requestId.current) setIsLoadingMore(false);
    }
  }, [listPath, view, search, direction, tasks, hasMore, nextCursor, isLoading, isLoadingMore]);

  /** Drops rows locally, such as a deleted email, without reloading and losing the loaded pages. */
  const removeItems = useCallback((shouldRemove: (item: MailSummary) => boolean) => {
    setItems(previous => previous.filter(item => !shouldRemove(item)));
  }, []);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  useEffect(() => () => {
    requestId.current++;
  }, []);

  return { items, hasMore, isLoading, isLoadingMore, error, loadMore, reload: loadFirstPage, removeItems };
};
